import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientService } from '../services/patientService';
import type { Patient } from '@clinic-os/shared/types';

export const PATIENTS_KEY = ['patients'] as const;

export function usePatients() {
  return useQuery({
    queryKey: PATIENTS_KEY,
    queryFn: () => patientService.getAll(),
  });
}

export function usePatient(id: string) {
  return useQuery({
    queryKey: [...PATIENTS_KEY, id],
    queryFn: () => patientService.getById(id),
    enabled: !!id,
  });
}

export function usePatientSearch(query: string) {
  return useQuery({
    queryKey: [...PATIENTS_KEY, 'search', query],
    queryFn: () => patientService.search(query),
    enabled: query.length > 1,
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>) => patientService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: PATIENTS_KEY }),
  });
}

export function useUpdatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Patient> }) =>
      patientService.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
      qc.invalidateQueries({ queryKey: [...PATIENTS_KEY, id] });
    },
  });
}
